// Path: ./src/app/pages/MerchantPage/index.tsx
import { Box, Stack } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
import { SearchInput } from "../../components/SearchInputField";
import { adminApiService } from "../../../utils/api";
import {
  GfMerchant,
  MuiFilterRequestDto,
} from "@swagger/typescript-fetch-goflyer";

import { GridFilterModel, GridRowSelectionModel } from "@mui/x-data-grid";
import { MuiConfirmationModal } from "../../components/MuiConfirmationModal";
import { useTranslation } from "react-i18next";

import { messages } from "../../../locales/messages";
import { useNavigate } from "react-router-dom";
import { toastError, toastSuccess } from "../../../utils/pageUtil/toast";
import { TableData } from "../../../utils/interface/tableData";
import { AccountCardWrapper } from "../../components/AccountCardWrapper";
import { useMerchantTableColumns } from "./useMerchantTableColumns";
import { SetupPageTopAppBar } from "../../../utils/commonStyledComponents/commonStyled";
import { BlackFontTransparentButton } from "../../../utils/commonStyledComponents/commonMuiStyled";
import {
  LeftButtonSection,
  RightSearchSection,
  TableWrapper,
  Container,
} from "./styled";
import { PermissionComponentWrapper } from "../../components/PermissionComponentWrapper";
import { GoFlyerAdminContext } from "../../../contextApi/context";
import { SUPERADMIN } from "../../../utils/constant/stringConstant";
import { useAuthHeaders } from "../../../hooks/useAuthHeaders";
import { useQueryParams } from "../../../hooks/useQueryParams";
import { ListTableQueryPagination } from "../../components/ListTable/ListTableQueyPagination";
import { muiFilterModelHasActiveItems } from "../../../utils/muiGridFilterModel";
import { useLatestRequestGuard } from "../../../hooks/useLatestRequestGuard";
import { guardedListFetch } from "../../../utils/guardedListFetch";
import { SettingKey } from "@swagger/typescript-fetch-goflyer";
import { routes } from "../../../utils/constant/navigationRoutes";

const MerchantPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useContext(GoFlyerAdminContext);
  const authHeaders = useAuthHeaders();

  const userSettings = state.gfAdminUser?.settings || [];
  const tableConfigSetting = userSettings.find(
    (s) => s?.setting?.settingKey === SettingKey.TableMerchants,
  );
  const defaultPageSize = tableConfigSetting?.value?.pageLimit || 100;

  const {
    paginationModel,
    sortModel,
    searchText,
    setQueryParams,
    handleTableChange,
  } = useQueryParams(defaultPageSize);

  const [paginationLoading, setPaginationLoading] = useState(false);
  const listRequestGuard = useLatestRequestGuard();
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);
  const [merchantListData, setMerchantListData] =
    useState<TableData<GfMerchant>>();
  const [open, setOpen] = useState(false);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });

  const columns = useMerchantTableColumns();
  /**
   * Opens a confirmation modal to delete a Upload Merchant.
   *
   * @param value - The Upload Merchant data to be deleted.
   */
  const handleOpenDelete = () => {
    setOpen(true);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const fetchMerchantList = async () => {
    await guardedListFetch(
      listRequestGuard,
      setPaginationLoading,
      async () => {
        const conditions = [];
        const merchantList =
          state.user?.parsedRoles?.name === SUPERADMIN
            ? []
            : state.user?.merchant?.map((value) => {
                return `${value.id}`;
              }) || [];

        if (merchantList && merchantList.length) {
          conditions.push(`id||$in||${merchantList?.join(",")}`);
        }

        return adminApiService.getManyBaseGfMerchantAdminControllerGfMerchant(
          undefined,
          undefined,
          conditions?.length ? conditions : undefined,
          undefined,
          sortModel?.length
            ? [`${sortModel[0].field},${sortModel[0]?.sort?.toUpperCase()}`]
            : [`updatedAt,DESC`],
          undefined,
          paginationModel?.pageSize,
          0,
          paginationModel?.page,
          undefined,
        );
      },
      (MerchantList) => {
        setMerchantListData((prev) => ({
          ...prev,
          rows: MerchantList.data,
          total: MerchantList?.total,
        }));
      },
    );
  };

  const fetchMerchantListWithFilter = async (
    activeFilterModel: GridFilterModel,
  ) => {
    await guardedListFetch(
      listRequestGuard,
      setPaginationLoading,
      async () => {
        const body: MuiFilterRequestDto = {
          filterModel: {
            items: activeFilterModel.items.map((item) => ({
              field: item.field,
              operator: item.operator,
              value: item.value,
              id: item.id,
            })),
            logicOperator: activeFilterModel.logicOperator as any,
            quickFilterValues: activeFilterModel.quickFilterValues,
            quickFilterLogicOperator:
              activeFilterModel.quickFilterLogicOperator as any,
          },
          page: paginationModel.page,
          pageSize: paginationModel.pageSize,
          sortModel: sortModel?.map((s) => ({
            field: s.field,
            sort: s.sort as any,
          })),
        };
        return adminApiService.gfMerchantAdminControllerFilterWithMuiGrid(
          body,
          authHeaders,
        );
      },
      (response) => {
        if (!response) return;
        setMerchantListData((prev) => ({
          ...prev,
          rows: response.rows,
          total: response.total,
        }));
      },
    );
  };

  const handleFilterModelChange = (newFilterModel: GridFilterModel) => {
    setFilterModel(newFilterModel);
  };

  const handleDeleteMerchant = async () => {
    if (rowSelectionModel?.length) {
      try {
        setPaginationLoading(true);
        const result = await rowSelectionModel?.map(async (value) => {
          return await adminApiService.deleteOneBaseGfMerchantAdminControllerGfMerchant(
            value as string,
            authHeaders,
          );
        });
        await Promise.all(result);

        if (searchText?.trim()?.length) {
          searchMerchantFn(searchText);
        } else {
          fetchMerchantList();
        }

        setPaginationLoading(false);
        handleCancel();
        toastSuccess(t(messages.Successfully_Deleted()));
      } catch (error) {
        console.log("error", error);
        setPaginationLoading(false);
        toastError(
          t(messages.There_is_an_error_please_Contact_Administrator()),
        );
      }
    }
  };

  const handleOnSearch = (value: string) => {
    setQueryParams({
      searchText: value,
      page: 1,
      sortField: null,
      sortOrder: null,
    });
  };

  const searchMerchantFn = async (searchValue: string) => {
    if (!searchValue?.length) return;

    await guardedListFetch(
      listRequestGuard,
      setPaginationLoading,
      async () =>
        adminApiService.gfMerchantAdminControllerSearch(
          searchValue,
          paginationModel.page,
          paginationModel.pageSize,
        ),
      (response) => {
        setMerchantListData((prev) => ({
          ...prev,
          rows: response?.data,
          total: response?.total,
        }));
      },
    );
  };

  const isShowingSearchResults =
    !muiFilterModelHasActiveItems(filterModel) && Boolean(searchText?.length);

  useEffect(() => {
    const hasActiveFilters = muiFilterModelHasActiveItems(filterModel);
    if (hasActiveFilters) {
      fetchMerchantListWithFilter(filterModel);
    } else if (searchText?.length) {
      searchMerchantFn(searchText);
    } else {
      fetchMerchantList();
    }
  }, [searchText, paginationModel, sortModel, filterModel]);

  return (
    <Container data-testid="merchantpage-test-id">
      <AccountCardWrapper title={"Merchant"}>
        <Box data-testid="view-page">
          <SetupPageTopAppBar>
            <LeftButtonSection>
              <Stack direction="row" spacing={2}>
                <PermissionComponentWrapper
                  requiredPermission={["create"]}
                  requiredPermissionGroup={["merchant"]}
                >
                  <BlackFontTransparentButton
                    variant="text"
                    startIcon={
                      <UploadFileIcon
                        style={{ color: "#ff8038", fontSize: 25 }}
                      />
                    }
                    onClick={() => {
                      navigate(routes.merchant.create);
                    }}
                    data-testid="create-button"
                  >
                    {t(messages.Create())}
                  </BlackFontTransparentButton>
                </PermissionComponentWrapper>
                <PermissionComponentWrapper
                  requiredPermission={["delete"]}
                  requiredPermissionGroup={["merchant"]}
                >
                  <BlackFontTransparentButton
                    variant="text"
                    startIcon={
                      <DeleteIcon
                        style={{
                          color: "#ff8038",
                          fontSize: 25,
                          opacity: !rowSelectionModel?.length ? 0.5 : 1,
                        }}
                      />
                    }
                    disabled={!rowSelectionModel?.length}
                    onClick={handleOpenDelete}
                    data-testid="delete-button"
                  >
                    {t(messages.Delete())}
                  </BlackFontTransparentButton>
                </PermissionComponentWrapper>
              </Stack>
            </LeftButtonSection>
            {(state.user?.parsedRoles?.name !== SUPERADMIN ||
              !state.user?.merchant?.length) && (
              <RightSearchSection>
                <SearchInput
                  onSearch={handleOnSearch}
                  initialValue={searchText}
                ></SearchInput>
              </RightSearchSection>
            )}
          </SetupPageTopAppBar>
          <TableWrapper>
            <ListTableQueryPagination
              loadFailed={listRequestGuard.loadFailed}
              sortingMode={isShowingSearchResults ? "client" : "server"}
              columns={columns}
              rows={merchantListData?.rows}
              total={paginationLoading ? 0 : merchantListData?.total}
              paginationModel={paginationModel}
              sortModel={sortModel}
              isLoading={paginationLoading}
              onTableChange={handleTableChange}
              dynamicHeightAdjuster={230}
              handleRowSelection={(value) => {
                setRowSelectionModel(value);
              }}
              filterModel={filterModel}
              onFilterModelChange={handleFilterModelChange}
            />
          </TableWrapper>

          <MuiConfirmationModal
            open={open}
            handleCancel={handleCancel}
            handleConfirm={handleDeleteMerchant}
            title={`${t(messages.Delete_Confirmation())}`}
            description={`
        ${t(messages.Are_you_sure_you_want_to_delete_These())} ${t(
          messages.Selected_Merchants(),
          { count: rowSelectionModel.length },
        )}?  ${t(messages.This_action_cannot_be_undone())}.
      `}
          />
        </Box>
      </AccountCardWrapper>
    </Container>
  );
};

export default MerchantPage;
